import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { PostalTask } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const TABS = ['PENDING', 'PRINTED', 'DISPATCHED', 'COMPLETED'] as const;

export function PostalQueue() {
  const { persona } = useAuth();
  const canSee = persona?.roles.includes('TEMPLATE_ADMIN') ?? false;
  const [tab, setTab] = useState<(typeof TABS)[number]>('PENDING');
  const [actionError, setActionError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<PostalTask | null>(null);
  const listQ = useApiQuery<{ items: PostalTask[] }>(canSee ? `/api/v1/notify/postal-tasks?status=${tab}&limit=100` : null);

  const advance = async (t: PostalTask, status: 'PRINTED' | 'DISPATCHED') => {
    setActionError(null);
    try {
      await api.post(`/api/v1/notify/postal-tasks/${t.id}/status`, { status });
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    }
  };

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Postal fallback queue" subtitle="Statutory notices that could not be proven delivered electronically" />
        <EmptyState title="No access" hint="This screen needs the TEMPLATE_ADMIN role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Postal fallback queue" subtitle="Statutory notices that exhausted the electronic delivery cascade" />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
              tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nothing here" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Recipient</th>
                <th className="table-head">Case</th>
                <th className="table-head">Reason</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{t.recipientId}</td>
                  <td className="table-cell text-xs">{t.caseRef ?? '—'}</td>
                  <td className="table-cell text-xs text-slate-500">{t.reason}</td>
                  <td className="table-cell">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex justify-end gap-1.5">
                      {t.status === 'PENDING' ? (
                        <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => advance(t, 'PRINTED')}>
                          Mark printed
                        </button>
                      ) : null}
                      {t.status === 'PRINTED' ? (
                        <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => advance(t, 'DISPATCHED')}>
                          Mark dispatched
                        </button>
                      ) : null}
                      {t.status === 'DISPATCHED' ? (
                        <button className="btn-success !px-2 !py-0.5 text-xs" onClick={() => setCompleting(t)}>
                          Complete (proof)
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {completing ? (
        <CompleteModal
          task={completing}
          onClose={() => setCompleting(null)}
          onDone={() => {
            setCompleting(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CompleteModal({ task, onClose, onDone }: { task: PostalTask; onClose: () => void; onDone: () => void }) {
  const [proofDocId, setProofDocId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/notify/postal-tasks/${task.id}/complete`, { proofDocId });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Completion failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Complete postal task — ${task.recipientId}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Proof-of-service document reference (DocVault)">
          <input
            className="field-input"
            value={proofDocId}
            onChange={(e) => setProofDocId(e.target.value)}
            placeholder="e.g. DOCV-POSTAL-2026-0031"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || proofDocId.trim().length < 3}>
            {busy ? 'Saving…' : 'Complete'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
